import { useState } from 'react';
import { type CardScanResult, scanBusinessCard } from '../lib/api';
import {
  type ContactFieldMap,
  type ContactFieldName,
  buildContactSubmission,
  hasAnyContact,
} from '../lib/contactFields';
import { CardScanSource } from './CardScanSource';
import { Icon } from './Icon';

/**
 * S1 Welcome card (R10 — showcase-conversation-synergy.md §4, client-scenarios.md Scenario 1
 * beats 1–3 / Scenario 2 beat 1).
 *
 * The greeting-as-scene that replaces the transcript-embedded contact form. It captures
 * name + company ONLY (§4.1 — two fields ≈ zero friction; a full form up front loses walk-ups).
 * Three fill modes, ranked for a B2B fair audience (§4.2):
 *   1. Business-card scan-to-fill (FEATURED) — camera/file → POST /v1/kiosk/card-scan → fields
 *      fill live with a `card` source affordance.
 *   2. Voice-fill live echo — ui.contact_field echoes render live with a DRAFT treatment (dashed
 *      underline + "hearing…") until status:'final'. Driven from the store (contactFields map).
 *   3. Typing — always available, never pushed. The quiet/noisy-hall/accessibility fallback.
 *
 * "Just browsing" skip is FIRST-CLASS (§4.1, Scenario 2) — dismisses to browse with no guilt UI.
 * Doubles as the cold-start mask: intentional-feeling for the ~5s before Gigi's first words.
 *
 * Design system: tokens only (bg-surface / bg-fill / text-ink / ring-line / brass), the Icon
 * primitive, .reveal motion (reduced-motion safe via tokens). Night theme by default (Live surface).
 */

type Props = {
  /** Live capture map from the store (voice/card echoes + typed edits). */
  fields: ContactFieldMap;
  /**
   * R12 (F4) — capture completed. When true the card shows a brass "got it" confirmation beat
   * before it auto-advances to browse (the container owns the timer). Prevents the card lingering
   * blank while Gigi captures name/company by voice (the field bug — the card stayed empty).
   */
  captured?: boolean;
  /** Hand-typed edit → typed final (authoritative; voice never clobbers it). */
  onType: (field: ContactFieldName, value: string) => void;
  /** Card-scan result → fill fields live (source:'card', status:'final'). */
  onCardScan: (fields: Partial<Record<ContactFieldName, string>>) => void;
  /** Continue → emit contact-submitted {fields, method} and yield the stage to browse. */
  onContinue: (
    fields: { name?: string; company?: string },
    method: 'card' | 'voice' | 'typed'
  ) => void;
  /** "Just browsing" → dismiss to browse, capturing nothing. */
  onSkip: () => void;
  /** conversation_id for the card-scan call (attaches the scan to this lead). */
  conversationId?: string;
  /** Greeting copy (falls back to a warm default). */
  greeting?: string;
  /** R25 — scan surface open/close → host mutes the mic + emits the §38 `card-modal` signal. */
  onScanSurfaceChange?: (active: boolean) => void;
};

type ScanState = 'idle' | 'scanning' | 'error';

export function WelcomeCard({
  fields,
  captured = false,
  onType,
  onCardScan,
  onContinue,
  onSkip,
  conversationId,
  greeting,
  onScanSurfaceChange,
}: Props) {
  const [scan, setScan] = useState<ScanState>('idle');
  const [scanConfidence, setScanConfidence] = useState<number | null>(null);

  async function handleFile(file: File) {
    setScan('scanning');
    setScanConfidence(null);
    try {
      const result: CardScanResult = await scanBusinessCard(file, conversationId);
      // Welcome card only uses name + company (§4.1); email/phone are for the S7 close card.
      onCardScan({ name: result.fields.name, company: result.fields.company });
      setScanConfidence(result.confidence);
      setScan('idle');
    } catch {
      // Never blocks — the visitor falls back to typing (§4.2 typing is always available).
      setScan('error');
    }
  }

  function handleContinue() {
    const { fields: submit, method } = buildContactSubmission(fields);
    onContinue(submit, method);
  }

  const canContinue = hasAnyContact(fields);

  // R13 U6 — the confirm beat. Once BOTH name and company have rendered (from any source), the
  // primary action becomes an explicit "That's right" confirmation (a tap = confirm → submit +
  // advance to the category grid). A server "yes" (fields marked final) auto-advances via the
  // container's both-final path; this button is the touch peer to that. Below both-present, the
  // button stays the neutral "Continue" (a single field is enough to proceed).
  const bothPresent =
    (fields.name?.value?.trim() ?? '') !== '' && (fields.company?.value?.trim() ?? '') !== '';

  // F4 — the visitor's display name once captured (name preferred; company as a fallback).
  const capturedName = fields.name?.value?.trim() || fields.company?.value?.trim() || '';

  return (
    <div className="reveal flex flex-col gap-6" data-testid="welcome-card">
      {/* Greeting — display serif, gallery tone (not "form"). Swaps to a captured/confirmed beat
          (brass check + acknowledgment) once capture completes, so the card never lingers blank
          during voice capture — then the container auto-advances to browse (F4). */}
      {captured ? (
        <div className="flex items-start gap-3" data-testid="welcome-captured" aria-live="polite">
          <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brass-soft ring-1 ring-brass/40">
            <Icon name="check" size={16} className="text-brass" />
          </span>
          <div className="flex flex-col gap-1">
            <p className="font-display text-2xl leading-tight text-ink">
              {capturedName ? `Lovely to meet you, ${capturedName}.` : 'Lovely to meet you.'}
            </p>
            <p className="text-sm text-subtle leading-relaxed">Let me show you around our range.</p>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-1.5">
          <p className="font-display text-2xl leading-tight text-ink">
            {greeting ?? 'Welcome — who do I have the pleasure of showing around?'}
          </p>
          <p className="text-sm text-subtle leading-relaxed">
            A name and company help me tailor what I show you. Or skip ahead and just browse — your
            call.
          </p>
        </div>
      )}

      {/* Featured: scan your business card (the hero path for a B2B fair).
          R14 B6 — a Take photo / Photo library / Browse files source sheet (block-level so the sheet
          anchors under the full-width hero button). */}
      <div className="flex flex-col gap-2">
        <CardScanSource
          onFile={(f) => void handleFile(f)}
          disabled={scan === 'scanning'}
          title="Scan your card from"
          block
          onScanSurfaceChange={onScanSurfaceChange}
        >
          {({ open, disabled }) => (
            <button
              type="button"
              data-testid="card-scan-btn"
              disabled={disabled}
              onClick={open}
              className="btn-accent inline-flex w-full items-center justify-center gap-2.5 rounded-xl px-6 py-4 text-base font-display shadow-soft transition-transform duration-2 ease-physical hover:scale-[1.01] active:scale-[0.99] disabled:opacity-60"
            >
              {scan === 'scanning' ? (
                <>
                  <span className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin" />
                  Reading your card…
                </>
              ) : (
                <>
                  <Icon name="camera" size={20} />
                  Scan your business card
                </>
              )}
            </button>
          )}
        </CardScanSource>
        {scan === 'error' && (
          <p className="text-xs text-terracotta" data-testid="card-scan-error">
            Couldn't read that card — no matter, you can type your details below.
          </p>
        )}
        {scanConfidence !== null && scanConfidence > 0 && scanConfidence < 0.75 && (
          <p className="text-xs text-subtle">
            Please check the details below — the card was a little hard to read.
          </p>
        )}
      </div>

      {/* Divider */}
      <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.18em] text-faint">
        <span className="h-px flex-1 bg-line" />
        or tell me / type
        <span className="h-px flex-1 bg-line" />
      </div>

      {/* Name + company — voice-fill echoes render live with a draft treatment. */}
      <div className="flex flex-col gap-3">
        <WelcomeField
          field="name"
          label="Name"
          placeholder="Ritika Sharma"
          state={fields.name}
          onType={onType}
        />
        <WelcomeField
          field="company"
          label="Company"
          placeholder="Gulf Décor Retail"
          state={fields.company}
          onType={onType}
        />
      </div>

      {/* Actions — Continue is optional; "Just browsing" is first-class. R13 U6: once both name +
          company are present the primary action becomes an explicit "That's right" confirm — a tap
          submits + yields to the category grid (the touch peer to a voiced "yes"). */}
      <div className="flex items-center gap-3 pt-1">
        <button
          type="button"
          data-testid={bothPresent ? 'welcome-confirm-btn' : 'welcome-continue-btn'}
          disabled={!canContinue}
          onClick={handleContinue}
          className="btn-primary inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-display disabled:opacity-40"
        >
          <Icon name="check" size={15} />
          {bothPresent ? "That's right" : 'Continue'}
        </button>
        <button
          type="button"
          data-testid="welcome-skip-btn"
          onClick={onSkip}
          className="btn-ghost inline-flex items-center gap-2 rounded-full px-5 py-3 text-sm"
        >
          Just browsing
        </button>
      </div>
    </div>
  );
}

/**
 * A single welcome field. Renders the live voice/card echo with a DRAFT treatment (dashed
 * underline + "hearing…" affordance) until final. Typing is always available and, on edit,
 * becomes a typed final (the store enforces that voice never clobbers it).
 */
function WelcomeField({
  field,
  label,
  placeholder,
  state,
  onType,
}: {
  field: ContactFieldName;
  label: string;
  placeholder: string;
  state: ContactFieldMap[ContactFieldName];
  onType: (field: ContactFieldName, value: string) => void;
}) {
  const id = `welcome-${field}`;
  const isDraft = state?.status === 'draft';
  const fromVoice = state?.source === 'voice';
  const value = state?.value ?? '';

  return (
    <div className="flex flex-col gap-1">
      <label
        htmlFor={id}
        className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-subtle"
      >
        {label}
        {isDraft && fromVoice && (
          <span
            className="inline-flex items-center gap-1 text-[10px] text-brass"
            data-testid={`${field}-draft`}
          >
            <Icon name="mic" size={10} />
            hearing…
          </span>
        )}
      </label>
      <input
        id={id}
        type="text"
        value={value}
        placeholder={placeholder}
        autoComplete={field === 'name' ? 'name' : 'organization'}
        onChange={(e) => onType(field, e.target.value)}
        aria-describedby={isDraft ? `${id}-draft` : undefined}
        className={`w-full rounded-lg bg-fill px-3 py-2.5 text-sm text-ink ring-1 placeholder:text-faint focus:outline-none focus-visible:ring-2 focus-visible:ring-brass transition-shadow ${
          isDraft ? 'ring-brass/40 [border-bottom-style:dashed] italic text-subtle' : 'ring-line'
        }`}
      />
    </div>
  );
}
